import type { Tag } from '../api/tagsApi';
export type { Tag };

// TF-406: sort order of the question pool in the exam composer.
export type QuestionSort = 'newest' | 'most_used' | 'difficulty';

export enum ExamStatus {
  DRAFT = 'draft',
  FINALIZED = 'finalized',
  EXPORTED = 'exported',
}

export interface ExamQuestion {
  id: number;
  question_id: number;
  position: number;
  points: number;
  section: string | null;
  question_text: string;
  question_type: string;
  difficulty: string;
  topic: string;
  bloom_level: number | null;
  review_status: string;
  options: string[] | null;
  correct_answer: string | null;
  explanation: string | null;
}

export interface Exam {
  id: number;
  title: string;
  course: string | null;
  exam_date: string | null;
  time_limit_minutes: number | null;
  allowed_aids: string | null;
  instructions: string | null;
  passing_percentage: number;
  total_points: number;
  status: ExamStatus;
  language: string;
  created_at: string;
  updated_at: string;
  question_count: number;
  default_document_ids: number[] | null;
  // TF-335: NULL ⇒ exam inherits the institution default (or stays without one).
  grading_scheme_id?: number | null;
  // TF-398: archive axis — orthogonal to status. archived_at !== null ⇒ archived.
  archived_at?: string | null;
  archived_by?: number | null;
  archive_reason?: string | null;
  // Whether the exam has submissions (guard for the delete button).
  has_submissions?: boolean;
}

export interface ExamDetail extends Exam {
  questions: ExamQuestion[];
}

export interface ExamListResponse { total: number; exams: Exam[]; }
export interface CreateExamRequest { title: string; course?: string; exam_date?: string; time_limit_minutes?: number; allowed_aids?: string; instructions?: string; passing_percentage?: number; language?: string; default_document_ids?: number[]; grading_scheme_id?: number | null; }
export interface UpdateExamRequest extends Partial<CreateExamRequest> { updated_at: string; }
export interface ApprovedQuestion { id: number; question_text: string; question_type: string; difficulty: string; topic: string; bloom_level: number | null; options: string[] | null; usage_count: number; tags?: Tag[]; }
export interface ApprovedQuestionsResponse { total: number; questions: ApprovedQuestion[]; }

// TF-405: read-only detail for the preview modal in the exam composer.
export interface ApprovedQuestionOption { text: string; is_correct: boolean; }
export interface ApprovedQuestionSourceDocument { id: number; title: string; }
export interface ApprovedQuestionCompetency { id: number; code: string; title: string; framework_id: number; module_code?: string | null; }
export interface ApprovedQuestionDetail {
  id: number;
  question_text: string;
  question_type: string;
  difficulty: string;
  topic: string;
  language?: string | null;
  bloom_level: number | null;
  ln_level: number | null;
  estimated_time_minutes: number | null;
  options: ApprovedQuestionOption[];
  correct_answer: string | null;
  explanation: string | null;
  usage_count: number;
  competency: ApprovedQuestionCompetency | null;
  source_documents: ApprovedQuestionSourceDocument[];
}

export interface DocumentWithQuestions {
  id: number;
  title: string;
  approved_question_count: number;
}

export interface AutoFillRequest {
  count?: number;
  topic?: string;
  difficulty?: string[];
  bloom_level_min?: number;
  question_types?: string[];
  exclude_question_ids?: number[];
  document_ids?: number[];
  // Composition mode fields
  target_points?: number;
  target_duration_minutes?: number;
  bloom_distribution?: Record<number, number>;
  difficulty_distribution?: Record<string, number>;
  preview?: boolean;
}

export interface DistributionResult {
  target_pct: number;
  achieved_pct: number;
  within_tolerance: boolean;
}

export interface ConstraintReport {
  points_target: number | null;
  points_achieved: number;
  duration_target: number | null;
  duration_achieved: number;
  bloom_distribution: Record<number, DistributionResult>;
  difficulty_distribution: Record<string, DistributionResult>;
  overall_satisfaction: number;
}

export interface ProposedQuestion {
  id: number;
  question_text: string;
  question_type: string;
  difficulty: string;
  topic: string;
  bloom_level: number | null;
  estimated_time_minutes: number | null;
  suggested_points: number;
}

export interface AutoComposePreview {
  mode: 'preview';
  questions: ProposedQuestion[];
  total_points: number;
  total_duration_minutes: number;
  constraint_report: ConstraintReport;
}

export function isAutoComposePreview(data: unknown): data is AutoComposePreview {
  return typeof data === 'object' && data !== null && 'mode' in data && (data as AutoComposePreview).mode === 'preview';
}
